import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, useAlert } from "@digital-services/ui";

import ErrorComponent from "@components/ErrorComponent";
import LoadingComponent from "@components/LoadingComponent";

import { ApplicationJsonModel } from "@applications/models/ApplicationJsonModel";
import { OrganizationUser } from "@organization/models/OrganizationUser";
import { EditApplicationAssociations } from "@users/applicationuser/components/EditApplicationAssociations";
import { SelectedApplication } from "@users/applicationuser/models/SelectedApplication";
import { ApplicationSimplified } from "@users/applicationuser/models/UserApplication";
import {
  useInviteApplicationUser,
  useRemoveUserApplication,
  useUserApplications
} from "@users/applicationuser/services/ApplicationUserService";
import { useUpdateOrganizationUser } from "@users/organizationuser/services/OrganizationUserService";

import { useOrganizationContext } from "@hooks/useOrganizationContext";

import { AssociatedApplicationRoles } from "../models/AssociatedApplicationRoles";
import { NameEmailRoles } from "./section-top/NameEmailRoles";

interface Props {
  organizationUser: OrganizationUser; // TBD Introduce context
  onCancel: () => void;
  onSuccess: () => void;
}

function areRolesEqual(orgRoles: string[], newRoles: string[]): boolean {
  if (orgRoles.length !== newRoles.length) return false;

  for (const role of orgRoles) {
    const index = newRoles.findIndex(item => item === role);
    if (index === -1) return false;
  }

  return true;
}

// Enters 5 TIMES
export function EditUserForm(props: Readonly<Props>) {
  const { organizationUser, onCancel, onSuccess } = props;

  const { t } = useTranslation(undefined, {
    keyPrefix: "organization.users"
  });

  const { t: tApplication } = useTranslation(undefined, {
    keyPrefix: "application.users"
  });
  const { t: tCommon } = useTranslation();

  const { organization } = useOrganizationContext();

  const { notifyError, notifySuccess } = useAlert();

  const { update } = useUpdateOrganizationUser(organizationUser.id);

  const { invite: inviteApplicationUser } = useInviteApplicationUser();
  const { deleteApp: deleteApplicationUser } = useRemoveUserApplication();

  const [selectedOrganizationRole, setSelectedOrganizationRole] =
    useState<string>(organizationUser.organizationRole);

  const [isValidOrganizationRole, setIsValidOrganizationRole] =
    useState<boolean>(true);

  const [selectedApplications, setSelectedApplications] = useState<
    SelectedApplication[]
  >([]);

  const [canSave, setCanSave] = useState<boolean>(false);

  const { userApplications, isLoading, error } = useUserApplications(
    organizationUser.id
  );

  const [isBusy, setIsBusy] = useState<boolean>(false);

  useEffect(() => {
    if (!userApplications) return;

    const items: SelectedApplication[] = [];

    for (const userApplication of userApplications) {
      const appData = userApplication?.data;
      const appError = userApplication?.error;

      if (appError) {
        continue;
        // MAYBE I will need to use .error, .status instead
      }

      items.push({
        applicationAssociation: {
          applicationId: appData.application.id,
          applicationName: appData.application.name,
          userRoles: appData.userRoles
        },
        isValid: true
      });
    }

    setSelectedApplications(items);
  }, [userApplications]);

  useEffect(() => {
    setCanSave(selectedApplications.every(item => item.isValid));
  }, [selectedApplications]);

  const updateApplicationAssociationsIfNeeded = async () => {
    for (const selectedApp of selectedApplications) {
      const orgApplicationAssociation = userApplications!.find(
        item =>
          item?.data?.application.id ===
          selectedApp.applicationAssociation.applicationId
      );

      if (!orgApplicationAssociation) {
        await addApplicationAssociation(selectedApp.applicationAssociation);
      } else if (
        !areRolesEqual(
          orgApplicationAssociation?.data?.userRoles || [],
          selectedApp.applicationAssociation.userRoles
        )
      ) {
        await updateApplicationAssociation(selectedApp.applicationAssociation);
      }
    }

    // Remove Application Associations that are no longer in the selected applications
    for (const userApp of userApplications!) {
      if (userApp?.error) {
        continue;
      }

      const app = userApp.data.application;

      const index = selectedApplications.findIndex(
        item => item.applicationAssociation.applicationId === app.id
      );
      if (index > -1) continue;

      await removeApplicationAssociation(app, organizationUser.id);
    }
  };

  const addApplicationAssociation = async (
    applicationAssociation: AssociatedApplicationRoles
  ) => {
    const { isSuccess } = await inviteApplicationUser(
      applicationAssociation.applicationId,
      organizationUser.id,
      applicationAssociation.userRoles
    );

    if (isSuccess) {
      notifySuccess({
        header: tApplication("add.success.message", {
          user: organizationUser.displayName,
          application: applicationAssociation.applicationName
        }),
        "data-testid": "application-association-save-success"
      });
    } else {
      notifyError({
        header: tApplication("add.error.message", {
          user: organizationUser.displayName,
          application: applicationAssociation.applicationName
        }),
        "data-testid": "application-association-save-error"
      });
    }
  };

  const updateApplicationAssociation = async (
    applicationAssociation: AssociatedApplicationRoles
  ) => {
    const { isSuccess } = await inviteApplicationUser(
      applicationAssociation.applicationId,
      organizationUser.id,
      applicationAssociation.userRoles
    );

    if (isSuccess) {
      notifySuccess({
        header: tApplication("update.success.message", {
          user: organizationUser.displayName,
          application: applicationAssociation.applicationName
        }),
        "data-testid": "application-association-save-success"
      });
    } else {
      notifyError({
        header: tApplication("update.error.message", {
          user: organizationUser.displayName,
          application: applicationAssociation.applicationName
        }),
        "data-testid": "application-association-save-error"
      });
    }
  };

  const removeApplicationAssociation = async (
    application: ApplicationSimplified,
    userId: string
  ) => {
    const { error } = await deleteApplicationUser(application.id, userId);

    if (error) {
      notifyError({
        header: tApplication("delete.error.message", {
          user: organizationUser.displayName,
          application: application.name
        }),
        "data-testid": "application-disassociation-save-error"
      });
    } else {
      notifySuccess({
        header: tApplication("delete.success.message", {
          user: organizationUser.displayName,
          application: application.name
        }),
        "data-testid": "application-disassociation-save-success"
      });
    }
  };

  const updateOrganizationRoleIfNeeded = async (): Promise<void> => {
    if (
      !isValidOrganizationRole ||
      selectedOrganizationRole === organizationUser.organizationRole
    ) {
      return;
    }

    const { isSuccess } = await update({
      OrganizationRole: selectedOrganizationRole
    });

    if (isSuccess) {
      notifySuccess({
        header: t("update.success.message", {
          user: organizationUser.displayName,
          organization: organization.name
        }),
        "data-testid": "edit-user-save-success"
      });
    } else {
      notifyError({
        header: t("update.error.message", {
          user: organizationUser.displayName,
          organization: organization.name
        }),
        "data-testid": "edit-user-save-error"
      });
    }
  };

  const saveChanges = () => {
    setIsBusy(true);
    updateOrganizationRoleIfNeeded()
      .then(() => {
        updateApplicationAssociationsIfNeeded()
          .then(() => {
            onSuccess();
          })
          .catch(() => {
            notifyError({
              header: t("save.error.message"),
              "data-testid": "application-association-save-error"
            });
          })
          .finally(() => {
            setIsBusy(false);
          });
      })
      .catch(() => {
        setIsBusy(false);
        notifyError({
          header: t("save.error.message"),
          "data-testid": "edit-user-save-error"
        });
      });
  };

  function handleRoleChange(newValue: string, isValid: boolean) {
    setSelectedOrganizationRole(newValue);
    setIsValidOrganizationRole(isValid);
  }

  function handleAppRemove(application: ApplicationJsonModel) {
    setSelectedApplications(current => {
      const applications = [...current];

      const index = applications.findIndex(
        item => item.applicationAssociation.applicationId === application.id
      );
      if (index > -1) applications.splice(index, 1);

      return applications;
    });
  }

  function handleAppRoleChange(
    applicationId: string,
    applicationName: string,
    applicationRoles: string[],
    isValid: boolean
  ) {
    const selectedAppRole = new AssociatedApplicationRoles(
      applicationId,
      applicationName,
      applicationRoles
    );

    setSelectedApplications(current => {
      const applications = [...current];

      const index = applications.findIndex(
        item => item.applicationAssociation.applicationId === applicationId
      );
      if (index > -1) applications.splice(index, 1);

      let newIsValid = isValid;

      if (selectedAppRole?.userRoles.length === 0) {
        // Use case with apps like "AMP" where no roles implemented/integrated
        newIsValid = true;
      }

      applications.push({
        applicationAssociation: selectedAppRole,
        isValid: newIsValid
      });

      return applications;
    });
  }

  if (error) return <ErrorComponent message={t("load.error.message")} />;

  return (
    <div
      className="flex h-6 min-h-min flex-col gap-10"
      data-testid="edit-user-content"
    >
      <NameEmailRoles
        handleRoleChange={handleRoleChange}
        organizationUser={organizationUser}
        organizationRole={selectedOrganizationRole}
        isBusy={isBusy}
      />

      {isLoading ? (
        <LoadingComponent message={t("load.message")} />
      ) : (
        <EditApplicationAssociations
          onAppRoleChange={handleAppRoleChange}
          onAppRemove={handleAppRemove}
          isDisabled={isBusy}
          userId={organizationUser.id}
          userApplications={userApplications ?? []}
        />
      )}

      <div
        className="flex flex-row-reverse gap-4"
        data-testid="edit-user-form-buttons"
      >
        <Button
          data-testid="save"
          variant="primary"
          isLoading={isBusy}
          disabled={isLoading || !canSave}
          onClick={saveChanges}
        >
          {tCommon("button.save.label")}
        </Button>
        <Button
          onClick={() => onCancel()}
          disabled={isBusy}
          data-testid="cancel"
        >
          {tCommon("button.cancel.label")}
        </Button>
      </div>
    </div>
  );
}
